import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronRight, ShieldCheck, Truck, Info, Lightbulb } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { BuyerAPI, formatPrice, PromoAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useSuppliers } from '../hooks/useSuppliers';
import { WILAYAS } from '../data/wilayas';

// Grouper le panier par fournisseur
const groupBySupplier = (cart) => {
  const groups = {};
  cart.forEach(it => {
    const sid = it.variant.supplier_id || it.variant.supplierId || 'unknown';
    if (!groups[sid]) groups[sid] = { supplier_id: sid, items: [], subtotal: 0 };
    groups[sid].items.push(it);
    groups[sid].subtotal += it.variant.price * it.qty;
  });
  return Object.values(groups);
};

// Calculer la livraison d'un groupe selon politique fournisseur
const calcShipping = (group, supplierPolicies) => {
  const policy = supplierPolicies[group.supplier_id];
  if (!policy) return { fee: 0, note: 'Livraison à convenir avec le fournisseur', unknown: true };
  const { free_shipping_threshold: threshold, shipping_fee: fee } = policy;
  if (threshold === null || threshold === undefined) {
    return { fee: 0, note: 'Livraison à convenir avec le fournisseur', unknown: true };
  }
  if (threshold === 0 || group.subtotal >= threshold) {
    return { fee: 0, note: 'Livraison offerte', free: true };
  }
  const remaining = threshold - group.subtotal;
  return {
    fee: fee || 0,
    note: `Livraison offerte dès ${formatPrice(threshold)} (il manque ${formatPrice(remaining)})`,
    free: false,
  };
};

const Checkout = ({ cart, setCart }) => {
  const { user }  = useAuth();
  const navigate  = useNavigate();
  const { toast } = useToast();
  const { byId: suppliersById } = useSuppliers();
  const { t } = useTranslation();
  const [busy,              setBusy]              = useState(false);
  const [form, setForm] = useState({
    full_name: user?.name || '', phone: '', address: '', city: '', wilaya: '', notes: '',
  });

  const groups   = groupBySupplier(cart);
  const subtotal = cart.reduce((s, it) => s + it.variant.price * it.qty, 0);

  // Politiques de livraison dérivées du hook useSuppliers (source unique : GET /api/suppliers).
  // Remplace l'ancien appel à /api/suppliers/{sid}/shipping qui n'existe pas côté backend.
  const supplierPolicies = suppliersById;

  // Opportunités d'économie : groupes sous le seuil de livraison gratuite
  const savingsOpportunities = groups
    .map((g) => {
      const sup = suppliersById[g.supplier_id];
      if (!sup) return null;
      const threshold = sup.free_shipping_threshold;
      const fee = sup.shipping_fee || 0;
      if (!threshold || threshold <= 0 || fee <= 0) return null;
      if (g.subtotal >= threshold) return null;
      return {
        supplier_id: g.supplier_id,
        supplier_name: sup.name,
        needed: threshold - g.subtotal,
        savings: fee,
      };
    })
    .filter(Boolean);
  const totalPotentialSavings = savingsOpportunities.reduce((s, o) => s + o.savings, 0);

  const totalShipping = groups.reduce((s, g) => s + calcShipping(g, supplierPolicies).fee, 0);
  const [promo, setPromo] = useState(null); // {code, discount, free_shipping}
  const [promoInput, setPromoInput] = useState('');
  const [promoBusy, setPromoBusy] = useState(false);
  const promoDiscount = promo?.discount || 0;
  const effectiveShipping = promo?.free_shipping ? 0 : totalShipping;
  const total = Math.max(0, subtotal + effectiveShipping - promoDiscount);

  const applyPromo = async () => {
    if (!promoInput.trim()) return;
    setPromoBusy(true);
    try {
      const r = await PromoAPI.validate(promoInput.trim(), subtotal);
      setPromo(r);
      toast({ title: '✅ Code appliqué', description: `Remise : ${formatPrice(r.discount)}${r.free_shipping ? ' + livraison gratuite' : ''}` });
    } catch (e) {
      setPromo(null);
      toast({ title: 'Code invalide', description: e?.response?.data?.detail || 'Code introuvable ou expiré' });
    } finally { setPromoBusy(false); }
  };
  const removePromo = () => { setPromo(null); setPromoInput(''); };

  if (!user) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center">
        <p className="text-[#5A5247]">Connectez-vous pour passer commande.</p>
        <Link to="/connexion" className="inline-block mt-4 text-[#1F4D3F] font-medium hover:underline">Se connecter</Link>
      </div>
    );
  }

  if (cart.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center text-[#5A5247]">
        Panier vide. <Link to="/" className="text-[#1F4D3F] hover:underline">Parcourir le catalogue</Link>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    if (!form.wilaya) { toast({ title: 'Erreur', description: 'Sélectionnez votre wilaya.' }); return; }
    setBusy(true);
    try {
      const order = await BuyerAPI.createOrder({
        items: cart.map(it => ({
          product_id:    it.product.id,
          variant_label: it.variant.label,
          qty:           it.qty,
          supplier_id:   it.variant.supplier_id || it.variant.supplierId,
        })),
        shipping_address: form,
      });
      setCart([]);
      toast({ title: '✅ Commande confirmée !', description: `Numéro ${order.id}` });
      navigate('/mes-commandes');
    } catch (err) {
      toast({ title: 'Erreur', description: err.response?.data?.detail || 'Commande impossible' });
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <nav className="flex items-center gap-1.5 text-sm text-[#7A6F5C] mb-4">
        <Link to="/" className="hover:text-[#1F4D3F]">Accueil</Link>
        <ChevronRight className="w-3.5 h-3.5" />
        <Link to="/panier" className="hover:text-[#1F4D3F]">Panier</Link>
        <ChevronRight className="w-3.5 h-3.5" />
        <span className="text-[#2D2A26]">Validation</span>
      </nav>
      <h1 className="font-serif text-3xl lg:text-4xl font-semibold text-[#1F4D3F] mb-6">Valider la commande</h1>

      {/* Bannière astuce d'économie */}
      {savingsOpportunities.length > 0 && (
        <div
          className="mb-6 rounded-xl border border-amber-200 bg-amber-50 p-4 flex items-start gap-3"
          data-testid="checkout-savings-tip"
        >
          <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
            <Lightbulb className="w-5 h-5 text-amber-700" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-medium text-amber-900">
              Astuce — vous pourriez économiser jusqu'à <strong>{formatPrice(totalPotentialSavings)}</strong> de frais de livraison
            </div>
            <ul className="mt-1.5 text-sm text-amber-900 space-y-1">
              {savingsOpportunities.map((o) => (
                <li key={o.supplier_id} className="flex items-start gap-1.5">
                  <span className="text-amber-700 mt-0.5">•</span>
                  <span>
                    Ajoutez <strong>{formatPrice(o.needed)}</strong> chez <strong>{o.supplier_name}</strong> pour économiser{' '}
                    <strong>{formatPrice(o.savings)}</strong> de livraison
                  </span>
                </li>
              ))}
            </ul>
            <Link
              to="/"
              className="inline-flex items-center gap-1 mt-2 text-sm font-medium text-[#1F4D3F] hover:underline"
              data-testid="checkout-continue-shopping"
            >
              ← Continuer mes achats
            </Link>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-6">
        <form onSubmit={submit} className="bg-white border border-[#E8E0D0] rounded-2xl p-6 space-y-4">
          <h2 className="font-serif text-xl font-semibold text-[#1F4D3F]">{t('checkout.shipping_info')}</h2>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-[#2D2A26] block mb-1">{t('checkout.full_name')} *</label>
              <Input required value={form.full_name} onChange={e => setForm({ ...form, full_name: e.target.value })} />
            </div>
            <div>
              <label className="text-sm font-medium text-[#2D2A26] block mb-1">{t('checkout.phone')} *</label>
              <Input required value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="0555 00 00 00" />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-[#2D2A26] block mb-1">{t('checkout.address')} *</label>
            <Input required value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} placeholder="N° rue, quartier..." />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-[#2D2A26] block mb-1">{t('checkout.city')} *</label>
              <Input required value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} />
            </div>
            <div>
              <label className="text-sm font-medium text-[#2D2A26] block mb-1">{t('checkout.wilaya')} *</label>
              <select required value={form.wilaya} onChange={e => setForm({ ...form, wilaya: e.target.value })}
                className="w-full h-10 border border-[#E8E0D0] rounded-md px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#1F4D3F]">
                <option value="">—</option>
                {WILAYAS.map(w => <option key={w} value={w}>{w}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-[#2D2A26] block mb-1">{t('checkout.notes')}</label>
            <Input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Instructions pour le livreur..." />
          </div>

          {/* Livraison par fournisseur */}
          <div className="space-y-2">
            {groups.map((g, i) => {
              const shipping = calcShipping(g, supplierPolicies);
              const supName  = supplierPolicies[g.supplier_id]?.name || `Fournisseur ${i + 1}`;
              return (
                <div key={g.supplier_id}
                  className={`flex items-start gap-3 rounded-lg px-4 py-3 text-sm border
                    ${shipping.free ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                    : shipping.unknown ? 'bg-[#FAF7F2] border-[#E8E0D0] text-[#5A5247]'
                    : 'bg-[#FBF3DE] border-amber-200 text-amber-800'}`}>
                  <Truck className="w-4 h-4 mt-0.5 shrink-0" />
                  <div>
                    <span className="font-medium">{supName}</span>
                    <span className="mx-2">·</span>
                    <span>{shipping.note}</span>
                    {!shipping.free && !shipping.unknown && shipping.fee > 0 && (
                      <span className="ml-2 font-semibold">({formatPrice(shipping.fee)})</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="bg-[#F5F1EA] rounded-lg p-4 flex items-start gap-3">
            <ShieldCheck className="w-5 h-5 text-[#1F4D3F] mt-0.5 shrink-0" />
            <div className="text-sm text-[#5A5247]">
              <span className="font-medium text-[#2D2A26]">{t('checkout.payment_cod')} (COD)</span> — Vous payez en espèces lors de la réception.
            </div>
          </div>

          <Button type="submit" data-testid="checkout-confirm-btn" disabled={busy} className="w-full h-12 bg-[#1F4D3F] hover:bg-[#173B30] text-white text-base">
            {busy ? t('common.loading') : `${t('checkout.place_order')} · ${formatPrice(total)}`}
          </Button>
        </form>

        <aside className="bg-white border border-[#E8E0D0] rounded-2xl p-6 h-fit lg:sticky lg:top-[180px]">
          <h2 className="font-serif text-xl font-semibold text-[#1F4D3F] mb-4">{t('checkout.order_summary')}</h2>

          {/* Articles groupés par fournisseur */}
          {groups.map((g, gi) => {
            const shipping = calcShipping(g, supplierPolicies);
            const supName  = supplierPolicies[g.supplier_id]?.name || `Fournisseur ${gi + 1}`;
            const supCity  = supplierPolicies[g.supplier_id]?.city;
            return (
              <div key={g.supplier_id} className="mb-4 pb-4 border-b border-[#F5F1EA] last:border-0">
                <div className="text-xs font-medium text-[#7A6F5C] uppercase tracking-wider mb-2 flex items-center gap-1">
                  <Truck className="w-3 h-3" />
                  {supName}{supCity ? ` · ${supCity}` : ''}
                </div>
                <div className="space-y-2 mb-2">
                  {g.items.map((it) => (
                    <div key={`${it.product.id}-${it.variant.supplier_id || ''}-${it.variant.label}`} className="flex items-center gap-2 text-sm">
                      <img src={it.product.image} alt="" className="w-9 h-9 rounded object-cover border border-[#E8E0D0]"
                        onError={e => { e.target.style.display = 'none'; }} />
                      <div className="flex-1 min-w-0">
                        <div className="text-[#2D2A26] font-medium truncate">{it.product.name}</div>
                        <div className="text-xs text-[#7A6F5C]">{it.variant.label} · x{it.qty}</div>
                      </div>
                      <div className="text-[#1F4D3F] font-medium whitespace-nowrap text-xs">{formatPrice(it.variant.price * it.qty)}</div>
                    </div>
                  ))}
                </div>
                <div className="flex justify-between text-xs text-[#7A6F5C]">
                  <span>Livraison</span>
                  <span className={shipping.free ? 'text-emerald-700 font-medium' : shipping.unknown ? 'italic' : 'text-amber-700'}>
                    {shipping.free ? 'Offerte' : shipping.unknown ? 'À convenir' : shipping.fee > 0 ? formatPrice(shipping.fee) : 'À convenir'}
                  </span>
                </div>
              </div>
            );
          })}

          <div className="space-y-2 text-sm border-t border-[#F5F1EA] pt-3">
            <div className="flex justify-between text-[#5A5247]">
              <span>Sous-total</span><span>{formatPrice(subtotal)}</span>
            </div>
            <div className="flex justify-between text-[#5A5247]">
              <span>Livraison</span>
              <span className={effectiveShipping === 0 ? 'text-emerald-700 font-medium' : ''}>
                {promo?.free_shipping && totalShipping > 0 ? (
                  <><s className="text-[#C9BFAE]">{formatPrice(totalShipping)}</s> <span className="text-emerald-700">Offerte</span></>
                ) : effectiveShipping === 0 ? 'Offerte / À convenir' : formatPrice(effectiveShipping)}
              </span>
            </div>
            {promoDiscount > 0 && (
              <div className="flex justify-between text-emerald-700 font-medium">
                <span>Code {promo.code}</span><span>− {formatPrice(promoDiscount)}</span>
              </div>
            )}
          </div>

          {/* Code promo */}
          <div className="mt-3 pt-3 border-t border-[#F5F1EA]">
            {promo ? (
              <div className="flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 text-sm" data-testid="promo-applied">
                <span className="text-emerald-800">Code <strong>{promo.code}</strong> appliqué</span>
                <button onClick={removePromo} className="text-xs text-rose-600 hover:underline">Retirer</button>
              </div>
            ) : (
              <div className="flex gap-2">
                <Input
                  value={promoInput}
                  onChange={(e) => setPromoInput(e.target.value.toUpperCase())}
                  placeholder="Code promo"
                  className="h-10 text-sm flex-1 font-mono"
                  data-testid="promo-input"
                />
                <Button type="button" onClick={applyPromo} disabled={promoBusy || !promoInput.trim()} variant="outline" className="border-[#1F4D3F] text-[#1F4D3F]" data-testid="promo-apply">
                  {promoBusy ? '...' : 'Appliquer'}
                </Button>
              </div>
            )}
          </div>
          <div className="flex justify-between font-serif text-xl font-semibold text-[#1F4D3F] mt-3 pt-3 border-t border-[#F5F1EA]">
            <span>Total</span>
            <span>
              {formatPrice(total)}
              {groups.some(g => calcShipping(g, supplierPolicies).unknown) && (
                <span className="text-xs text-[#7A6F5C] font-normal ml-1">+ livraison</span>
              )}
            </span>
          </div>

          {groups.some(g => calcShipping(g, supplierPolicies).unknown) && (
            <div className="mt-3 flex items-start gap-2 text-xs text-[#7A6F5C] bg-[#FAF7F2] rounded-lg p-3">
              <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              La livraison sera confirmée par le fournisseur après votre commande.
            </div>
          )}
        </aside>
      </div>
    </div>
  );
};

export default Checkout;
